import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  AsyncValidatorFn,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TypeSelection } from '@components/type-selector/1.0';
import { Tenant } from '@generated/tcea/models/tenant';
import {
  ImageUploadId,
  ImageUploadValidationFactory,
  nonChannelImageIds,
} from '@helpers/image-upload-validation-factory';
import { BrandService } from '@services/brand/1.0/brand.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  ChangeSetItemType,
  FormattedChangesForItem,
  ItemChanges,
} from '@interfaces/change-sets/1.0';
import { CatalogImageData } from '@interfaces/catalog/1.0/catalog-image';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import {
  catalogImageCvaValidate,
  syncFileOnlyAsyncValidator,
} from '@helpers/catalog-image-cva-validate';
import { NgIf, NgClass, NgFor, KeyValuePipe } from '@angular/common';
import { TypeSelectorComponent } from '../type-selector/1.0/type-selector.component';
import { ChangeSetNoticeComponent } from '../change-set-notice/change-set-notice.component';
import { FileUploadComponent } from '../file-upload/1.0/file-upload.component';
import { MatError } from '@angular/material/form-field';
@Component({
  selector: 'txp-catalog-item-images',
  templateUrl: './catalog-item-images.component.html',
  styleUrls: ['./catalog-item-images.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogItemImagesComponent),
      multi: true,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => CatalogItemImagesComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgClass,
    TypeSelectorComponent,
    NgFor,
    ChangeSetNoticeComponent,
    FileUploadComponent,
    MatError,
    TranslateModule,
    KeyValuePipe,
  ],
})
export class CatalogItemImagesComponent
  implements ControlValueAccessor, OnInit, OnDestroy, AsyncValidator
{
  @Input() itemChanges: ItemChanges;
  @Input() selectedVersion: ChangeSetItemType;
  @Input() changeSetImageKey: string;
  @Input() preventSelector: boolean;
  @Input() preventInfoTitle: boolean;
  @Input() imageUploadIds: ImageUploadId[]; // will display image upload option if provided
  @Input() allowCrop = false;
  @Input() allowResize = false;
  @Input() autoResize = false;
  @Input() disabled = false;

  brand: Tenant;
  form: UntypedFormGroup;
  changeFn: any;
  touchedFn: any;
  onValidatorChange?: () => void;
  selectedImage: CatalogImageData;
  imageTypes: TypeSelection[];
  ignoreChangeEvent: boolean;
  imageUploadId = ImageUploadId;

  private pendingValue: Record<string, unknown> | null = null;
  private unsubscribe = new Subject<void>();
  private controlUnsubscribe = new Subject<void>();
  private applyGeneration = 0;
  private pendingApplyTimeout: ReturnType<typeof setTimeout> | null = null;
  private fileAsyncValidators = new Map<string, AsyncValidatorFn>();

  constructor(
    private brandService: BrandService,
    private translateService: TranslateService,
  ) {
    this.form = new UntypedFormGroup({});
  }

  ngOnInit() {
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        takeUntil(this.unsubscribe),
        filter((brand) => brand.isSet),
      )
      .subscribe(async (brand) => {
        if (this.imageUploadIds) {
          this.brand = brand;
          this.setupImages(this.imageUploadIds);
        }
      });

    this.form.statusChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter(() => !this.ignoreChangeEvent),
      )
      .subscribe(() => {
        this.onValidatorChange?.();
      });
  }

  ngOnDestroy() {
    this.applyGeneration++;
    this.clearPendingApply();
    this.ignoreChangeEvent = true;
    this.controlUnsubscribe.next();
    this.controlUnsubscribe.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        filter(() => !this.ignoreChangeEvent),
      )
      .subscribe(this.changeFn);
  }

  registerOnTouched(fn: any): void {
    this.touchedFn = fn;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  getChangeSetDecoratorKey(formControlKey: string): string {
    return this.changeSetImageKey || formControlKey;
  }

  hasImageChanged(
    formForControl: AbstractControl,
    decoratorKey: string,
    versionToCompare: string,
    itemChanges: FormattedChangesForItem,
  ): boolean {
    const { decorators, images } = itemChanges || {};
    const sameVersion = versionToCompare === this.selectedVersion;

    return (
      !!decorators?.find((decorator) => {
        return decorator.field === 'images' && decorator.key === decoratorKey;
      }) ||
      images?.[decoratorKey] ||
      (sameVersion && formForControl?.dirty)
    );
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form.disable({ emitEvent: false }) : this.form.enable({ emitEvent: false });
  }

  validate(_control: AbstractControl): Observable<ValidationErrors | null> {
    return catalogImageCvaValidate(
      this.form,
      this.unsubscribe,
      this.translateService.instant('form.error.fieldsAreInvalid'),
    );
  }

  writeValue(data: any): void {
    this.pendingValue = data && typeof data === 'object' ? DeepCloneService.clone(data) : null;
    this.applyPendingValue();
  }

  onSelectImageType(data: CatalogImageData) {
    this.selectedImage = data;
  }

  setupImages(imageUploadIds: ImageUploadId[]) {
    const aggregators = this.brand?.aggregatorConfigurations || {};

    this.ignoreChangeEvent = true;
    this.imageTypes = [];
    this.fileAsyncValidators.clear();
    this.controlUnsubscribe.next();
    Object.keys(this.form.controls).forEach((key) => this.form.removeControl(key));

    const shouldSkipImageChannel = (id) => {
      // Check if this is a non-channel image (logo, large, small, etc.)
      if (nonChannelImageIds.includes(id)) {
        return false;
      }

      return !aggregators[id]?.enabled;
    };

    for (let i = 0, len = imageUploadIds.length; i < len; i++) {
      const id = imageUploadIds[i];

      if (shouldSkipImageChannel(id)) {
        continue;
      }

      const imageValidation = ImageUploadValidationFactory.get(id);
      this.imageTypes.push({
        label: this.translateService.instant('orders.grid.filters.orderSource.' + id),
        data: {
          id,
          allowedMimeTypes: imageValidation.allowedMimeTypes,
          instructions: imageValidation.instructions,
        },
      });
      const ctrl = new UntypedFormControl(undefined, imageValidation.validator);
      this.bindFileAsyncValidator(id, ctrl, imageValidation.asyncValidator);
      this.form.addControl(id, ctrl);
    }

    if (this.imageTypes[0]) {
      // hard code to select first one
      this.selectedImage = this.imageTypes[0].data;
    }

    // Re-apply value that may have arrived via writeValue before controls existed
    this.applyPendingValue();
  }

  private applyPendingValue(): void {
    this.clearPendingApply();
    const generation = ++this.applyGeneration;
    this.ignoreChangeEvent = true;
    this.form.reset(undefined, { emitEvent: false });
    if (this.pendingValue) {
      this.form.patchValue(DeepCloneService.clone(this.pendingValue), { emitEvent: false });
    }
    this.syncAllFileAsyncValidators();

    // workaround for using async validator that never resets status past PENDING on initialization
    // need to manually trigger it
    this.pendingApplyTimeout = setTimeout(() => {
      if (generation !== this.applyGeneration) {
        return;
      }
      // we don't want to trigger the onChange function because it will mark the control dirty
      this.ignoreChangeEvent = true;
      // emitEvent must stay on so async validators emit statusChanges; CVA
      // validate() waits on that. ignoreChangeEvent swallows the valueChanges.
      this.form.updateValueAndValidity();
      this.finishProgrammaticUpdate(generation);
    });
  }

  private finishProgrammaticUpdate(generation: number): void {
    Promise.resolve().then(() => {
      if (generation !== this.applyGeneration) {
        return;
      }
      this.form.markAsPristine();
      this.ignoreChangeEvent = false;
      this.pendingApplyTimeout = null;
    });
  }

  private bindFileAsyncValidator(
    id: string,
    ctrl: UntypedFormControl,
    asyncValidator?: AsyncValidatorFn,
  ): void {
    if (!asyncValidator) {
      return;
    }
    this.fileAsyncValidators.set(id, asyncValidator);
    ctrl.valueChanges
      .pipe(takeUntil(this.controlUnsubscribe), takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (syncFileOnlyAsyncValidator(ctrl, asyncValidator)) {
          ctrl.updateValueAndValidity();
        }
      });
  }

  private syncAllFileAsyncValidators(): void {
    this.fileAsyncValidators.forEach((asyncValidator, id) => {
      const ctrl = this.form.get(id);
      if (ctrl) {
        syncFileOnlyAsyncValidator(ctrl, asyncValidator);
      }
    });
  }

  private clearPendingApply(): void {
    if (this.pendingApplyTimeout != null) {
      clearTimeout(this.pendingApplyTimeout);
      this.pendingApplyTimeout = null;
    }
  }
}
